import { ModuleFieldSchema, controlLabel, type ModuleField } from "shared"
import { Checkbox } from "@rawa/ui/Checkbox"
import { InputField } from "@rawa/ui/InputField"
import { TextArea } from "@rawa/ui/TextArea"
import { inspectorFor, type FieldEdit } from "@rawa/form-modules/inspectors"
import styles from "./FieldInspector.module.css"

/**
 * A field's properties: what every field has, then what its control adds.
 * Each change is reported as the edit to apply, never applied.
 */
export function FieldInspector({
  field,
  onEdit,
}: {
  field: ModuleField
  onEdit: (edit: FieldEdit) => void
}) {
  const { Inspector } = inspectorFor(field.control)
  return (
    <div className={styles.properties}>
      <BaseProperties field={field} onEdit={onEdit} />
      <Inspector field={field} onEdit={onEdit} />
    </div>
  )
}

/** What every field has, whatever its control: FieldBaseSchema's properties. */
function BaseProperties({
  field,
  onEdit,
}: {
  field: ModuleField
  onEdit: (edit: FieldEdit) => void
}) {
  // What is wrong with the name, as the schema the server applies sees it.
  const nameProblem = ModuleFieldSchema.safeParse(field).error?.issues.find(
    (issue) => issue.path.length === 1 && issue.path[0] === "label",
  )?.message
  return (
    <>
      <InputField
        id="field-name"
        label="Name"
        value={field.label}
        onChange={(label) => onEdit({ label })}
        error={nameProblem}
      />
      <dl className={styles.readOnlyProperty}>
        <dt>Field type</dt>
        <dd>{controlLabel(field.control)}</dd>
      </dl>
      <TextArea
        id="field-instructions"
        label="Field instructions"
        value={field.instructions ?? ""}
        // Blank is none: the field shows no instructions, so none are kept.
        onChange={(instructions) =>
          onEdit({
            instructions: instructions.trim() ? instructions : undefined,
          })
        }
        placeholder="Explain how to answer this field"
      />
      <Checkbox
        checked={field.required}
        label="Required"
        size="small"
        onChange={(required) => onEdit({ required })}
      />
    </>
  )
}
